import * as THREE from 'three';
import { CSM } from 'three/examples/jsm/csm/CSM.js';

// Cascaded shadow maps for the legacy renderer: the sun becomes two or three directional lights,
// each shadowing one slice of the view (near slice sharp, far slice coarse), through three.js's
// CSM helper. The helper expects every material to be registered by hand; instead its shader
// chunks are switched on for every lit material at once (the defines go into the chunk itself and
// the uniforms into the shader libraries), so walls, people, trees, vehicles and whatever is made
// later all take the cascades without plumbing. Only the legacy renderer: the node renderer has
// CSMShadowNode for the same job.

export class Cascades {
  // sky: the Sky whose sun this replaces (its light is switched off; its direction, colour and
  // strength are copied each frame). options: cascades, mapSize, maxFar (metres of view with
  // shadows), fade (blend between cascades).
  constructor(scene, camera, sky, { cascades = 3, mapSize = 2048, maxFar = 480, fade = true } = {}) {
    this.sky = sky;
    this.camera = camera;
    const csm = new CSM({
      parent: scene,
      camera,
      cascades,
      maxFar,
      mode: 'practical',
      shadowMapSize: mapSize,
      lightDirection: new THREE.Vector3(0.3, -1, 0.2).normalize(),
      lightIntensity: sky.sun.intensity,
      lightNear: 1,
      lightFar: 1400, // the slice is at most ~700 m deep along the light, plus the margin
      lightMargin: 300, // metres of casters behind the slice (hills, tall buildings)
      shadowBias: -0.00005,
    });
    csm.fade = fade;
    csm.updateFrustums();
    this.csm = csm;
    this.frame = 0;
    // the far slices are coarse anyway: half the map and a refresh every other frame
    csm.lights.forEach((light, i) => {
      if (i === 0) return;
      light.shadow.mapSize.set(mapSize / 2, mapSize / 2);
      light.shadow.autoUpdate = false;
    });
    // the cascade splits are fixed by the camera's near plane and maxFar: one set of uniforms for
    // every shader, cloned into each program as it is built
    const breaks = [];
    csm._getExtendedBreaks(breaks);
    const uniforms = { CSM_cascades: { value: breaks }, cameraNear: { value: camera.near }, shadowFar: { value: Math.min(camera.far, maxFar) } };
    for (const lib of ['lambert', 'phong', 'standard', 'physical']) Object.assign(THREE.ShaderLib[lib].uniforms, uniforms);
    const defines = `#define USE_CSM 1\n#define CSM_CASCADES ${cascades}\n${fade ? '#define CSM_FADE 1\n' : ''}`;
    THREE.ShaderChunk.lights_pars_begin = defines + THREE.ShaderChunk.lights_pars_begin;
    // the one sun of the sky gives way to the cascade lights
    sky.sun.visible = false;
    sky.sun.castShadow = false;
    this.on = true;
  }

  // Call once a frame after the sky has moved its sun.
  update() {
    const sky = this.sky;
    const csm = this.csm;
    // the slices follow the camera's lens: recut them when the window or the field of view changes
    const lens = this.camera.aspect * 1000 + this.camera.fov;
    if (lens !== this.lens) {
      this.lens = lens;
      csm.updateFrustums();
    }
    csm.lightDirection.copy(sky.dir).negate().normalize();
    const shadows = sky.sun.intensity > 0.4;
    for (const light of csm.lights) {
      light.color.copy(sky.sun.color);
      light.intensity = sky.sun.intensity;
      light.castShadow = shadows;
    }
    csm.update();
    // both biases are set in metres: the depth bias is a fraction of the shadow camera's whole
    // depth range (a fraction fit for a 900 m camera swallowed every caster under 2 m at 3000 m),
    // and the normal bias scales with the metres each texel covers, so the near slice keeps
    // contact shadows tight and the far slice does not stripe
    this.frame++;
    csm.lights.forEach((light, i) => {
      const cam = light.shadow.camera;
      const texel = (cam.right - cam.left) / light.shadow.mapSize.x;
      light.shadow.normalBias = Math.max(0.04, texel * 2.5);
      light.shadow.bias = -0.06 / (cam.far - cam.near);
      // every map is drawn in the first frames, so no shader samples a map that is not there yet
      if (i > 0) light.shadow.needsUpdate = this.frame < 4 || (this.frame + i) % 2 === 0;
    });
  }

  // The maps' memory and the number of shadow passes, for the perf overlay.
  describe() {
    const csm = this.csm;
    return `${csm.cascades}x${csm.shadowMapSize}`;
  }
}
